import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { User, LogOut, LayoutDashboard, ChevronDown } from 'lucide-react';

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header 
      className="sticky top-0 z-50 bg-white border-b border-[#E8E8E8]"
      data-testid="header"
    >
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center"
            data-testid="header-logo"
          >
            <span className="font-extrabold text-2xl text-[#7B2CBF]">
              Zoopbowl
            </span>
          </Link>

          {/* Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            <Link 
              to="/deals" 
              className="px-4 py-2 text-[#242E30] hover:text-[#7B2CBF] font-medium rounded-lg hover:bg-[#F5F5F5] transition-colors"
              data-testid="nav-deals"
            >
              Browse Deals
            </Link>
            <Link 
              to="/restaurants" 
              className="px-4 py-2 text-[#242E30] hover:text-[#7B2CBF] font-medium rounded-lg hover:bg-[#F5F5F5] transition-colors"
              data-testid="nav-restaurants"
            >
              Restaurants
            </Link>
          </nav>

          {/* Auth */}
          <div className="flex items-center gap-3">
            {user === null ? (
              <div className="w-8 h-8 rounded-full bg-[#F5F5F5] animate-pulse" />
            ) : user === false ? (
              <>
                <Link
                  to="/login"
                  className="px-4 py-2 text-[#242E30] hover:text-[#7B2CBF] font-medium rounded-lg hover:bg-[#F5F5F5] transition-colors"
                  data-testid="nav-login"
                >
                  Log in
                </Link>
                <Link
                  to="/register"
                  className="bg-[#7B2CBF] text-white hover:bg-[#5A189A] px-5 py-2.5 font-semibold rounded-lg transition-colors"
                  data-testid="nav-register"
                >
                  Sign up
                </Link>
              </>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className="flex items-center gap-2 px-4 py-2 text-[#242E30] font-medium rounded-lg hover:bg-[#F5F5F5] transition-colors"
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#7B2CBF] flex items-center justify-center text-white font-semibold text-sm">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <span className="hidden sm:inline">{user.name}</span>
                    <ChevronDown className="h-4 w-4 text-[#595959]" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {user.role === 'restaurant' && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  {user.role === 'admin' && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        Admin Panel
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
